// ============================================================
// v8 — the Card-Authoring Studio (BOLT-012).
//
// Author a seeker card and watch it CLIP a real, recently-played
// boundary live, with the auto-cost + validator verdict beside it,
// then export a Pack to share. Runs the SAME QuickJS-WASM sandbox the
// server uses (in-browser), on SYNTHETIC actors only — NO geolocation,
// NO socket / live game state (renders outside SocketProvider, like the
// v7 How-to-Play page). The default map is the most recent real play
// area, baked in from a replay (packs/boundaries → DEFAULT_BOUNDARY).
// ============================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import { DEFAULT_BOUNDARY } from '../../shared/packs/boundaries';
import {
  isCardSandboxReady,
  loadCardSandbox,
  runCardScriptSync,
  type CardRunResult,
} from '../../shared/sandbox/cardSandbox';
import { validateCard, type ValidationVerdict } from '../../shared/validateCard';
import { costCard, type CostReport } from '../../shared/costCard';
import { PACK_SCHEMA_VERSION, type CardDef, type PackManifest, type RevealClass } from '../../shared/pack';
import type { Boundary, LngLat, Zone } from '../../shared/protocol';

const STARTER = `// Author a seeker card. ctx.S / ctx.H are {lat, lng};
// geo.* builds regions (opaque ids). Return { op, region }.
const r = geo.feet(500);
const ring = geo.disk(ctx.S, r);
return geo.distance(ctx.S, ctx.H) <= r
  ? { op: 'keep',   region: ring }   // INSIDE  → keep the ring
  : { op: 'remove', region: ring };  // OUTSIDE → cut the ring`;

const REVEAL_CLASSES: RevealClass[] = ['binary-disk', 'wedge', 'half-plane', 'region-match', 'blob', 'none'];

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'studio-card';

/** Build a shareable Pack from an authored card (pure — unit-tested). */
export function buildPackFromCard(card: CardDef, boundary?: Boundary): PackManifest {
  return {
    schemaVersion: PACK_SCHEMA_VERSION,
    id: slug(card.label),
    name: card.label || 'Studio Card',
    author: 'studio',
    license: 'CC0-1.0',
    description: card.description || card.label,
    ...(boundary ? { boundary } : {}),
    deck: [card],
  };
}

// ---------------- tiny equirectangular projection of the boundary ----------------
const W = 360;
const HT = 260;
const PAD = 14;

function bboxOf(b: Boundary): [number, number, number, number] {
  const ring = b.geometry.coordinates[0];
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const [lng, lat] of ring) {
    minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  }
  return [minLng, minLat, maxLng, maxLat];
}

export function CardStudio({ onClose }: { onClose: () => void }): JSX.Element {
  const boundary = DEFAULT_BOUNDARY.boundary as Boundary;
  const bb = useMemo(() => bboxOf(boundary), [boundary]);
  const sx = (lng: number) => PAD + ((lng - bb[0]) / (bb[2] - bb[0])) * (W - 2 * PAD);
  const sy = (lat: number) => PAD + ((bb[3] - lat) / (bb[3] - bb[1])) * (HT - 2 * PAD);
  const invX = (x: number) => bb[0] + ((x - PAD) / (W - 2 * PAD)) * (bb[2] - bb[0]);
  const invY = (y: number) => bb[3] - ((y - PAD) / (HT - 2 * PAD)) * (bb[3] - bb[1]);

  const [source, setSource] = useState(STARTER);
  const [label, setLabel] = useState('My Radius');
  const [revealClass, setRevealClass] = useState<RevealClass>('binary-disk');
  const [ready, setReady] = useState(isCardSandboxReady());
  const [S, setS] = useState<LngLat>({ lat: (bb[1] + bb[3]) / 2, lng: (bb[0] + bb[2]) / 2 });
  const [H, setH] = useState<LngLat>({ lat: bb[1] + (bb[3] - bb[1]) * 0.66, lng: bb[0] + (bb[2] - bb[0]) * 0.4 });
  const [preview, setPreview] = useState<CardRunResult | null>(null);
  const [verdict, setVerdict] = useState<ValidationVerdict | null>(null);
  const [cost, setCost] = useState<CostReport | null>(null);
  const [exported, setExported] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<'S' | 'H' | null>(null);

  // Load the WASM sandbox in-browser (no GPS, no socket).
  useEffect(() => {
    if (!ready) loadCardSandbox().then(() => setReady(true)).catch(() => {});
  }, [ready]);

  const def: CardDef = useMemo(
    () => ({ id: 'studio-card', deck: 'seeker', label, description: label, defaultCount: 1, behavior: { kind: 'script', lang: 'js', source, revealClass } }),
    [label, source, revealClass],
  );

  // Live clip — cheap, re-runs on every drag.
  useEffect(() => {
    if (ready) setPreview(runCardScriptSync(source, { S, H, Z: boundary as Zone, boundary: boundary as Zone, seed: 1 }));
  }, [ready, source, S, H, boundary]);

  // Verdict + auto-cost — heavier (Monte-Carlo), so keyed on the SOURCE, not on drags.
  useEffect(() => {
    if (!ready) return;
    setVerdict(validateCard(def, { samples: 12 }));
    setCost(costCard(def, { samples: 60 }));
    setExported(null);
  }, [ready, source, revealClass]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMove = (e: React.PointerEvent) => {
    if (!drag.current || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const y = ((e.clientY - rect.top) / rect.height) * HT;
    const p = { lat: invY(y), lng: invX(x) };
    if (drag.current === 'S') setS(p);
    else setH(p);
  };

  const ringPath = (ring: Position[]) => 'M' + ring.map(([lng, lat]) => `${sx(lng).toFixed(1)} ${sy(lat).toFixed(1)}`).join(' L ') + ' Z';
  const zonePaths = (z: Zone | undefined): string[] => {
    if (!z) return [];
    const g = z.geometry as Polygon | MultiPolygon;
    const polys = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
    return polys.map((poly) => poly.map(ringPath).join(' '));
  };

  const exportPack = () => {
    const card: CardDef = { ...def, id: slug(label), cost: cost?.cost };
    setExported(JSON.stringify(buildPackFromCard(card, boundary), null, 2));
  };

  const publishable = !!verdict?.ok;

  return (
    <div className="screen studio" data-testid="card-studio" data-boundary={DEFAULT_BOUNDARY.id}>
      <div className="screen__inner studio__inner">
        <header className="studio__head">
          <h1>Card Studio</h1>
          <button className="btn btn--ghost" data-testid="studio-close" onClick={onClose}>Close</button>
        </header>
        <p className="studio__map-name">Map: {DEFAULT_BOUNDARY.name} <span className="studio__muted">(real played boundary · no GPS)</span></p>

        <svg
          ref={svgRef}
          className="studio__map"
          viewBox={`0 0 ${W} ${HT}`}
          data-testid="studio-map"
          onPointerMove={onMove}
          onPointerUp={() => (drag.current = null)}
          onPointerLeave={() => (drag.current = null)}
        >
          <path d={ringPath(boundary.geometry.coordinates[0] as Position[])} className="studio__boundary" />
          {zonePaths(preview?.zone).map((d, i) => (
            <path key={i} d={d} className="studio__zone" />
          ))}
          <circle
            data-testid="studio-pin-S"
            cx={sx(S.lng)}
            cy={sy(S.lat)}
            r={9}
            className="studio__pin studio__pin--s"
            onPointerDown={() => { drag.current = 'S'; }}
          />
          <circle
            data-testid="studio-pin-H"
            cx={sx(H.lng)}
            cy={sy(H.lat)}
            r={9}
            className="studio__pin studio__pin--h"
            onPointerDown={() => { drag.current = 'H'; }}
          />
        </svg>

        <div className="studio__meta">
          <input className="studio__label" data-testid="studio-label" value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Card name" />
          <select data-testid="studio-reveal" value={revealClass} onChange={(e) => setRevealClass(e.target.value as RevealClass)} aria-label="Reveal class">
            {REVEAL_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <textarea
          className="studio__editor"
          data-testid="studio-source"
          spellCheck={false}
          value={source}
          onChange={(e) => setSource(e.target.value)}
          rows={9}
        />

        <div className="studio__readout">
          <span data-testid="studio-cut">Live cut: {preview?.ok ? `${(preview.cutPct ?? 0).toFixed(1)}%` : ready ? '—' : 'loading sandbox…'}</span>
          <span data-testid="studio-cost">Auto-cost: {cost ? `${cost.cost} hider card${cost.cost === 1 ? '' : 's'}` : '—'}</span>
          <span
            data-testid="studio-publishable"
            className={`studio__badge ${publishable ? 'studio__badge--ok' : 'studio__badge--bad'}`}
          >
            {verdict ? (publishable ? 'Publishable ✓' : 'Not publishable') : '…'}
          </span>
        </div>

        {verdict && verdict.violations.length > 0 && (
          <ul className="studio__violations" data-testid="studio-violations">
            {verdict.violations.map((v) => (
              <li key={v.code}><strong>{v.code}</strong>: {v.detail}</li>
            ))}
          </ul>
        )}

        <div className="studio__actions">
          <button className="btn" data-testid="studio-export" onClick={exportPack} disabled={!ready}>Export Pack</button>
        </div>
        {exported && <textarea className="studio__pack" data-testid="studio-pack" readOnly value={exported} rows={6} />}
      </div>
    </div>
  );
}
